import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth.store';
import { useThemeStore } from './store/theme.store';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/auth/Login';
import DashboardPage from './pages/dashboard/Dashboard';
import AddStudentPage from './pages/students/AddStudent';
import StudentProfilePage from './pages/students/StudentProfile';
import ClassesPage from './pages/classes/Classes';
import ClassFormPage from './pages/classes/ClassFormPage';
import SectionsPage from './pages/sections/Sections';
import SectionFormPage from './pages/sections/SectionFormPage';
import BatchesPage from './pages/batches/Batches';
import BatchFormPage from './pages/batches/BatchFormPage';
import TeachersPage from './pages/teachers/Teachers';
import TeacherFormPage from './pages/teachers/TeacherFormPage';
import AttendancePage from './pages/attendance/Attendance';
import AdmissionsPage from './pages/admissions/Admissions';
import OCRReviewPage from './pages/ocr/OCRReview';
import ReportsPage from './pages/reports/Reports';
import DocumentsPage from './pages/documents/Documents';
import FormBuilderPage from './pages/settings/FormBuilder';
import SettingsPage from './pages/settings/Settings';
import PrintPage from './pages/print/Print';
import ProtectedRoute from './components/common/ProtectedRoute';
import ModuleClassesPage from './pages/modules/ModuleClasses';
import ModuleClassHubPage from './pages/modules/ModuleClassHub';
import ModuleStudentListPage from './pages/modules/ModuleStudentList';
import ModuleStudentDetailPage from './pages/modules/ModuleStudentDetail';
import ExamMarksPage from './pages/exams/ExamMarks';
import AdminInstitutesPage from './pages/admin/Institutes';
import BulkImportPage from './pages/admin/BulkImport';
import ExpensesPage from './pages/expenses/Expenses';
import RevenuePage from './pages/revenue/Revenue';

function DashboardRedirect() {
  const { user } = useAuthStore();
  if (user?.role === 'super_admin') return <Navigate to="/admin/institutes" replace />;
  return <DashboardPage />;
}

function App() {
  const { checkAuth } = useAuthStore();
  const { initTheme } = useThemeStore();

  useEffect(() => {
    checkAuth();
    initTheme();
  }, [checkAuth, initTheme]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          <Route path="/" element={<DashboardRedirect />} />
          <Route path="/dashboard" element={<DashboardRedirect />} />
          {/* Redirect old student routes to module flow */}
          <Route path="/students" element={<ModuleClassesPage />} />
          <Route path="/students/add" element={<AddStudentPage />} />
          <Route path="/students/:id/edit" element={<AddStudentPage />} />
          <Route path="/students/:id" element={<StudentProfilePage />} />
          <Route path="/modules/:module/classes" element={<ModuleClassesPage />} />
          <Route path="/modules/:module/classes/:classId" element={<ModuleClassHubPage />} />
          <Route path="/modules/:module/classes/:classId/batches/:batchId" element={<ModuleStudentListPage />} />
          <Route path="/modules/:module/classes/:classId/batches/:batchId/students/:studentId" element={<ModuleStudentDetailPage />} />
          <Route path="/modules/:module/classes/:classId/batches/:batchId/students/:studentId/edit" element={<AddStudentPage />} />
          <Route path="/modules/:module/classes/:classId/status/:status" element={<ModuleStudentListPage />} />
          <Route path="/modules/:module/classes/:classId/enquiries" element={<ModuleStudentListPage />} />
          <Route path="/modules/:module/classes/:classId/exams/:examId" element={<ExamMarksPage />} />
          <Route path="/modules/:module/students/:studentId" element={<ModuleStudentDetailPage />} />
          <Route path="/classes" element={<ClassesPage />} />
          <Route path="/classes/create" element={<ClassFormPage />} />
          <Route path="/classes/:id/edit" element={<ClassFormPage />} />
          <Route path="/sections" element={<SectionsPage />} />
          <Route path="/sections/create" element={<SectionFormPage />} />
          <Route path="/sections/:id/edit" element={<SectionFormPage />} />
          <Route path="/batches" element={<BatchesPage />} />
          <Route path="/batches/create" element={<BatchFormPage />} />
          <Route path="/batches/:id/edit" element={<BatchFormPage />} />
          <Route path="/teachers" element={<TeachersPage />} />
          <Route path="/teachers/create" element={<TeacherFormPage />} />
          <Route path="/teachers/:id/edit" element={<TeacherFormPage />} />
          <Route path="/attendance" element={<AttendancePage />} />
          <Route path="/attendance/mark" element={<AttendancePage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/revenue" element={<RevenuePage />} />
          <Route path="/admissions" element={<AdmissionsPage />} />
          <Route path="/admissions/upload" element={<AdmissionsPage />} />
          <Route path="/ocr-review" element={<OCRReviewPage />} />
          <Route path="/ocr-review/:jobId" element={<OCRReviewPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/form-builder/admission" element={<FormBuilderPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/print/:type/:id" element={<PrintPage />} />
          <Route path="/admin/institutes" element={<AdminInstitutesPage />} />
          <Route path="/admin/bulk-import" element={<BulkImportPage />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
